import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Theme initialization - runs before React renders
const THEME_KEY = "zampa-theme";
const getSystemTheme = () => {
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
};

const initTheme = () => {
  const savedTheme = localStorage.getItem(THEME_KEY) || 'system';
  const effectiveTheme = savedTheme === 'system' ? getSystemTheme() : savedTheme;
  
  if (effectiveTheme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

// Initialize theme immediately
initTheme();

// Listen for system theme changes
if (window.matchMedia) {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const savedTheme = localStorage.getItem(THEME_KEY) || 'system';
    if (savedTheme === 'system') {
      initTheme();
    }
  });
}

// Pages
import Landing from "@/pages/LandingNew";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import PetDocument from "@/pages/PetDocument";
import Fascicolo from "@/pages/FascicoloApple";
import Pets from "@/pages/Pets";
import Documents from "@/pages/Documents";
import VetSearch from "@/pages/VetSearch";
import Marketplace from "@/pages/Marketplace";
import Services from "@/pages/Services";
import Bookings from "@/pages/Bookings";
import Settings from "@/pages/Settings";
import LostPets from "@/pages/LostPets";
import Emergency from "@/pages/Emergency";
import SharedPetView from "@/pages/SharedPetView";
import Shop from "@/pages/Shop";
import Tips from "@/pages/Tips";
import ProfessionalRegister from "@/pages/ProfessionalRegister";
import AdminProfessionals from "@/pages/AdminProfessionals";
import AdminDashboard from "@/pages/AdminDashboard";
import ProfessionalPortal from "@/pages/ProfessionalPortal";
import UserProfile from "@/pages/UserProfile";
import Notifications from "@/pages/Notifications";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import AdoptionPage from "@/pages/AdoptionPage";
import CalendarPage from "@/pages/CalendarPage";
import LandingWhoop from "@/pages/LandingWhoop";

// Veterinary Portal Pages
import VetAuth from "@/pages/VetAuth";
import VetDashboard from "@/pages/VetDashboard";
import VetPatientDetail from "@/pages/VetPatientDetail";
import VetCalendar from "@/pages/VetCalendar";
import AdminVets from "@/pages/AdminVets";
import AdminKB from "@/pages/AdminKB";

// Components
import BottomNav from "@/components/BottomNav";
import SideNav from "@/components/SideNav";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import JobTracker from "@/components/JobTracker";

// Auto-detect backend URL based on current domain
// In production (zampa.one, zampa.app), use the same domain
// In preview, use the env variable
const getBackendUrl = () => {
  const hostname = window.location.hostname;
  
  // Production domains - use same origin
  if (hostname === 'zampa.one' || hostname === 'zampa.app' || hostname === 'www.zampa.one' || hostname === 'www.zampa.app') {
    return window.location.origin;
  }
  
  // Preview or development - use env variable
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Helper function to make authenticated API calls
const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = {
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });
};

// Auth Context
import { createContext, useContext } from "react";

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    const hasSessionId = window.location.hash?.includes('session_id=') ||
                         window.location.search?.includes('session_id=') ||
                         window.location.href.includes('session_id=');
    
    if (hasSessionId) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await authFetch(`${API}/auth/me`);
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('session_token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await authFetch(`${API}/auth/logout`, {
        method: 'POST'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('session_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Handle job completion - refresh current page data
  const handleJobComplete = useCallback((job) => {
    // If user is on the fascicolo page for this pet, trigger a refresh
    if (job.pet_id && location.pathname.includes(`/fascicolo/${job.pet_id}`)) {
      // Dispatch a custom event that Fascicolo can listen to
      window.dispatchEvent(new CustomEvent('job-completed', { detail: job }));
    }
  }, [location.pathname]);
  
  // Check URL for session_id (OAuth callback) - check both hash and search params
  const hasSessionId = location.hash?.includes('session_id=') || 
                       location.search?.includes('session_id=') ||
                       window.location.href.includes('session_id=');
  
  if (hasSessionId && location.pathname !== '/auth/callback') {
    // Redirect to auth callback to process the session
    return <AuthCallback />;
  }
  
  // Pages that don't need any nav
  const noNavPages = ['/', '/auth/callback'];
  const publicPages = ['/emergency', '/lost-pets', '/shop'];
  
  // Show side nav on authenticated app pages (desktop)
  // Note: /documents is fine, but /pets/:id/document should hide nav
  const showSideNav = user && 
                      !noNavPages.includes(location.pathname) && 
                      !hasSessionId &&
                      !location.pathname.startsWith('/shared/') &&
                      !location.pathname.startsWith('/fascicolo/') &&
                      !location.pathname.match(/\/pets\/.*\/document/);
  
  // Show bottom nav on authenticated app pages (mobile) - exclude fascicolo which has its own nav
  const showBottomNav = showSideNav;

  return (
    <div className="flex min-h-screen overflow-x-hidden">
      {showSideNav && <SideNav />}
      <main className={`flex-1 overflow-x-hidden ${showSideNav ? 'lg:ml-64' : ''}`}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/fascicolo/:petId" element={
          <ProtectedRoute>
            <Fascicolo />
          </ProtectedRoute>
        } />
        <Route path="/pets" element={
          <ProtectedRoute>
            <Pets />
          </ProtectedRoute>
        } />
        <Route path="/pets/:petId" element={
          <ProtectedRoute>
            <Pets />
          </ProtectedRoute>
        } />
        <Route path="/vets" element={
          <ProtectedRoute>
            <VetSearch />
          </ProtectedRoute>
        } />
        <Route path="/marketplace" element={
          <ProtectedRoute>
            <Marketplace />
          </ProtectedRoute>
        } />
        <Route path="/services" element={
          <ProtectedRoute>
            <Services />
          </ProtectedRoute>
        } />
        <Route path="/documents" element={
          <ProtectedRoute>
            <Documents />
          </ProtectedRoute>
        } />
        <Route path="/bookings" element={
          <ProtectedRoute>
            <Bookings />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/pets/:petId/document" element={
          <ProtectedRoute>
            <PetDocument />
          </ProtectedRoute>
        } />
        <Route path="/lost-pets" element={<LostPets />} />
        <Route path="/emergency" element={<Emergency />} />
        <Route path="/shared/:shareToken" element={<SharedPetView />} />
        <Route path="/shop" element={<Shop />} />
        
        {/* Calendar */}
        <Route path="/calendar" element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        } />
        
        {/* Adoption - Public */}
        <Route path="/adozioni" element={<AdoptionPage />} />
        <Route path="/adozioni/:dogId" element={<AdoptionPage />} />
        
        <Route path="/professional/register" element={<ProfessionalRegister />} />
        <Route path="/professional/portal" element={<ProfessionalPortal />} />
        <Route path="/admin/professionals" element={
          <ProtectedRoute>
            <AdminProfessionals />
          </ProtectedRoute>
        } />
        <Route path="/admin/dashboard" element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/tips" element={
          <ProtectedRoute>
            <Tips />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <Notifications />
          </ProtectedRoute>
        } />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        
        {/* Veterinary Portal Routes */}
        <Route path="/vet/login" element={<VetAuth />} />
        <Route path="/vet/register" element={<VetAuth />} />
        <Route path="/vet/dashboard" element={<VetDashboard />} />
        <Route path="/vet/patient/:petId" element={<VetPatientDetail />} />
        <Route path="/vet/calendar" element={<VetCalendar />} />
        
        {/* Admin Vet Verification */}
        <Route path="/admin/vets" element={
          <ProtectedRoute>
            <AdminVets />
          </ProtectedRoute>
        } />
        
        {/* Admin Knowledge Base */}
        <Route path="/admin/kb" element={
          <ProtectedRoute>
            <AdminKB />
          </ProtectedRoute>
        } />
      </Routes>
      </main>
      {showBottomNav && <BottomNav />}
      {user && <JobTracker onJobComplete={handleJobComplete} />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <div className="App min-h-screen overflow-x-hidden" style={{ backgroundColor: '#FDFCF8' }}>
            <AppRouter />
            <Toaster position="top-center" richColors />
          </div>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
export { API, BACKEND_URL, authFetch };
